import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  House,
  Users,
  Buildings,
  Link as LinkIcon,
  GearSix,
  ArrowLeft,
  SignOut,
  List,
  X,
  ShieldCheck,
  CaretUpDown,
  Drop,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const adminNavItems = [
  { path: '/admin', label: 'Dashboard', icon: House, hint: 'Platform overview' },
  { path: '/admin/users', label: 'Users', icon: Users, hint: 'All accounts' },
  { path: '/admin/workspaces', label: 'Workspaces', icon: Buildings, hint: 'Tenants' },
  { path: '/admin/links', label: 'Links', icon: LinkIcon, hint: 'All short links' },
  { path: '/admin/settings', label: 'Settings', icon: GearSix, hint: 'Platform config' },
];

function getActivePage(pathname) {
  const match = adminNavItems.find((item) =>
    item.path === '/admin'
      ? pathname === '/admin'
      : pathname === item.path || pathname.startsWith(item.path + '/'),
  );
  return match || adminNavItems[0];
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const initials = (user?.name || user?.email || '?').slice(0, 2).toUpperCase();
  const activePage = getActivePage(location.pathname);

  return (
    <div className="min-h-screen bg-background flex">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:sticky top-0 inset-y-0 left-0 z-50 w-64 h-screen bg-white border-r border-border/70 flex flex-col py-6 transition-transform duration-200 shadow-[4px_0_24px_-12px_rgba(70,72,212,0.06)] ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="px-4 flex items-start justify-between gap-2">
          <Link
            to="/admin"
            data-testid="admin-sidebar-logo"
            className="flex items-center gap-3 min-w-0"
          >
            <span
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"
              aria-hidden
            >
              <Drop size={18} weight="fill" />
            </span>
            <span className="min-w-0 text-left">
              <span className="font-display font-extrabold tracking-tight text-primary text-lg block leading-tight">
                LinkFlow
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Admin Console
              </span>
            </span>
          </Link>
          <button
            className="md:hidden h-8 w-8 shrink-0 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Admin badge — same shape as workspace switcher in app */}
        <div className="px-4 pt-6 pb-2">
          <div
            className="w-full flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2.5 text-left"
            data-testid="admin-badge"
          >
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-primary text-primary-foreground">
              <ShieldCheck size={16} weight="fill" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/80">
                Admin Mode
              </span>
              <span className="block text-sm font-semibold text-foreground truncate">
                Platform controls
              </span>
            </span>
          </div>
        </div>

        <nav className="flex-1 px-3 pt-4 space-y-1 font-medium">
          <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Manage
          </p>
          {adminNavItems.map((item) => {
            const isActive = item.path === activePage.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`group flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all active:translate-x-0.5 ${
                  isActive
                    ? 'bg-primary/10 text-primary font-semibold border-r-[3px] border-primary -mr-px'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60 border-r-[3px] border-transparent'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={20} weight={isActive ? 'fill' : 'regular'} className="shrink-0 opacity-90" />
                <span className="flex-1 truncate">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Back to app card — visual parity with the upgrade card */}
        <div className="px-4 pb-3">
          <Link
            to="/dashboard"
            data-testid="admin-back-to-app"
            onClick={() => setSidebarOpen(false)}
            className="group block relative overflow-hidden rounded-xl border border-border/70 bg-gradient-to-br from-white to-primary/[0.06] p-4 transition-all hover:shadow-[0_18px_40px_-22px_rgba(255,84,54,0.18)]"
          >
            <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
              <ArrowLeft size={11} weight="bold" />
              Workspace
            </div>
            <p className="text-sm font-semibold leading-tight text-foreground">
              Back to user app
            </p>
            <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
              Manage your own links and analytics.
            </p>
          </Link>
        </div>

        {/* User menu */}
        <div className="border-t border-border/70 px-3 pt-3 mt-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm text-foreground hover:bg-muted/70 transition-colors"
                data-testid="user-menu-trigger"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-foreground text-white grid place-items-center text-xs font-semibold">
                    {initials}
                  </div>
                )}
                <span className="flex-1 min-w-0 text-left">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {user?.name || 'User'}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {user?.email}
                  </span>
                </span>
                <CaretUpDown size={14} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[220px]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Account
              </DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/dashboard">
                  <House size={14} className="mr-2" />
                  Back to user app
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-destructive focus:text-destructive"
              >
                <SignOut size={14} className="mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen bg-background">
        <header className="sticky top-0 z-30 h-16 border-b border-border/80 bg-white/80 backdrop-blur-md shadow-[0_20px_40px_rgba(70,72,212,0.04)]">
          <div className="h-full flex items-center gap-3 px-4 md:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden h-9 w-9 grid place-items-center rounded-lg border border-border/80 bg-white text-foreground hover:bg-muted/50"
              data-testid="admin-mobile-menu-btn"
              aria-label="Open sidebar"
            >
              <List size={18} />
            </button>

            <div className="flex items-center gap-3 min-w-0">
              <span className="chip bg-primary/10 text-primary border border-primary/15">
                <ShieldCheck size={12} weight="fill" />
                Admin
              </span>
              <span className="text-muted-foreground hidden sm:inline">/</span>
              <span className="text-sm font-semibold text-foreground truncate">
                {activePage.label}
              </span>
              <span className="hidden md:inline text-xs text-muted-foreground truncate">
                · {activePage.hint}
              </span>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Link
                to="/dashboard"
                className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-border bg-white text-sm font-semibold text-foreground hover:bg-muted/50 transition"
                data-testid="admin-topbar-back"
              >
                <ArrowLeft size={14} weight="bold" />
                User app
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
